import React, { forwardRef } from 'react';
import ReportPaper from './report/ReportPaper';
import ReportMarkedFigure from './report/ReportMarkedFigure';
import paper from './report/ReportPaper.module.css';
import styles from './BodyMapReportTemplate.module.css';
import { BODY_SILHOUETTE_VIEWBOX, getBodySilhouetteFigure } from '../bodySilhouette/bodySilhouetteFigures';
import { BODY_VIEW_OPTIONS } from '../bodySilhouette/bodyRegions';
import { MARKER_TYPES, formatDate, hasIntensity, markerTitle, markerTypeLabel } from '../../pages/appointment/components/bodyMap/bodyMapMarkers';

const FIGURE_WIDTH = 150;

// Mesmas cores da legenda do card (ColorPalette: error, main, success)
const TYPE_COLORS = {
  complaint: '#F44336',
  observation: '#5A80FB',
  assessed: '#4CAF50',
};

const typeColor = (type) => TYPE_COLORS[type] || TYPE_COLORS.observation;

/** Subtítulo e data da assinatura do mapa (`job` = { record, variant }). */
export function bodyMapReportMeta({ record }) {
  const count = ((record && record.markers) || []).length;
  const date = formatDate(record && record.markedAt);
  return {
    subtitle: `${date ? `Atendimento de ${date} · ` : ''}${count} ${count === 1 ? 'marcador' : 'marcadores'}`,
    date,
  };
}

/**
 * Miolo do PDF: legenda e, para cada vista com marcadores, a silhueta com os
 * números e a lista (região, tipo, intensidade, nota).
 *
 * `record`: { markedAt, markers }; `variant`: silhueta (male | female).
 */
export const BodyMapReportBody = ({ record, variant }) => {
  const markers = (record && record.markers) || [];
  const figure = getBodySilhouetteFigure(variant);

  const views = BODY_VIEW_OPTIONS
    .map((option) => ({
      ...option,
      src: option.key === 'back' ? figure.backSrc : figure.src,
      items: markers
        .map((marker, index) => ({ marker, number: index + 1 }))
        .filter(({ marker }) => marker.view === option.key),
    }))
    .filter((view) => view.items.length > 0);

  return (
    <>
      <div className={styles.legend}>
        {MARKER_TYPES.map((type) => (
          <span key={type.key} className={styles.legendItem}>
            <span className={paper.dot} style={{ background: typeColor(type.key) }} />
            {type.label}
          </span>
        ))}
      </div>

      {views.map((view) => (
        <div key={view.key} className={`${paper.section} ${styles.view}`}>
          <span className={paper.sectionTitle}>{view.label}</span>
          <div className={styles.layout}>
            <ReportMarkedFigure
              src={view.src}
              frame={BODY_SILHOUETTE_VIEWBOX}
              width={FIGURE_WIDTH}
              marks={view.items.map(({ marker, number }) => ({
                key: `m-${number}`,
                x: marker.x,
                y: marker.y,
                kind: 'point',
                color: typeColor(marker.type),
                number,
              }))}
            />
            <table className={paper.table}>
              <thead>
                <tr>
                  <th />
                  <th>Região</th>
                  <th>Tipo</th>
                  <th className={paper.num}>Intensidade</th>
                  <th>Nota</th>
                </tr>
              </thead>
              <tbody>
                {view.items.map(({ marker, number }) => (
                  <tr key={number}>
                    <td><span className={paper.number} style={{ background: typeColor(marker.type) }}>{number}</span></td>
                    <td>{markerTitle(marker)}</td>
                    <td>{markerTypeLabel(marker.type)}</td>
                    <td className={paper.num}>{hasIntensity(marker) ? `${marker.intensity}/10` : <span className={paper.muted}>—</span>}</td>
                    <td className={styles.note}>{marker.note || <span className={paper.muted}>—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </>
  );
};

/** PDF do mapa corporal de um atendimento: papel + miolo. */
const BodyMapReportTemplate = forwardRef(({ companyData, professional, patientName, ...job }, ref) => {
  const meta = bodyMapReportMeta(job);
  return (
    <ReportPaper
      ref={ref}
      companyData={companyData}
      professional={professional}
      patientName={patientName}
      title="Mapa corporal"
      subtitle={meta.subtitle}
      date={meta.date}
    >
      <BodyMapReportBody {...job} />
    </ReportPaper>
  );
});

BodyMapReportTemplate.displayName = 'BodyMapReportTemplate';

export default BodyMapReportTemplate;
